import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { DialogActionButton, Footer, Header, SubHeading, TitleText } from './EquipmentTable/styles';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { Color } from '@/types';
import {
  GetUserPreferencesDocument,
  useUpdateUserColumnPreferencesMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';

type DialogColumn = {
  name: string;
  key: string;
  isSelected: boolean;
  displayUnit?: string;
  unit?: string;
  templateFieldKey?: string;
  displayConfig?: { color?: Color[]; type?: string };
};

type Props = {
  open: boolean;
  columns: DialogColumn[];
  heading: string;
  subHeading: React.ReactNode | string;
  applyButtonText: string;
  applyButtonLoadingText?: string;
  cancelButtonText?: string;
  successMessage?: string;
  errorMessage?: string;
  userPreferenceKey?: string;
  userPreferenceType?: 'columns' | 'xlsx';
  onClose: () => void;
  onApply: (selectedColumnsKeys: string[]) => void | Promise<void>;
};

export const ColumnsSelectionDialog = ({
  open,
  columns,
  heading,
  subHeading,
  applyButtonText,
  applyButtonLoadingText,
  cancelButtonText,
  successMessage,
  errorMessage,
  userPreferenceKey,
  userPreferenceType,
  onClose,
  onApply,
}: Props) => {
  const [originalColumns, setOriginalColumns] = useState<DialogColumn[]>([]);
  const [displayedColumns, setDisplayedColumns] = useState<DialogColumn[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [applyLoading, setApplyLoading] = useState<boolean>(false);
  const { getUnitSuffix } = useUnitConversion();
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [updatePreferencesMutation] = useUpdateUserColumnPreferencesMutation();

  // Initialize columns with unit suffixes when component mounts or columns change
  useEffect(() => {
    if (open) {
      const mappedColumns = columns.map((column) => {
        const { displayUnit, unit, name, templateFieldKey } = column;
        const unitSuffix = getUnitSuffix({ unit: displayUnit ?? unit, field: templateFieldKey });
        const updatedName = unitSuffix ? `${name} (${unitSuffix})` : name;

        // Ensure Component column is always selected
        const isComponentColumn = name === 'Component' || updatedName === 'Component';
        return {
          ...column,
          name: updatedName,
          isSelected: isComponentColumn ? true : column.isSelected,
        };
      });

      setOriginalColumns(mappedColumns);
      setDisplayedColumns(mappedColumns);
      setSearchTerm('');
    }
  }, [columns, getUnitSuffix, open]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = originalColumns.filter((column) =>
        column.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setDisplayedColumns(filtered);
    } else {
      setDisplayedColumns(originalColumns);
    }
  }, [searchTerm, originalColumns]);

  const handleColumnChange = (key: string) => {
    // Prevent changing Component column selection
    const columnToChange = originalColumns.find((col) => col.key === key);
    if (columnToChange?.name === 'Component') {
      return; // Don't allow Component column to be unchecked
    }

    // Update both original and displayed columns
    const updatedOriginalColumns = originalColumns.map((column) => {
      if (column.key === key) return { ...column, isSelected: !column.isSelected };
      return column;
    });
    setOriginalColumns(updatedOriginalColumns);

    if (searchTerm) {
      const updatedDisplayedColumns = displayedColumns.map((column) => {
        if (column.key === key) return { ...column, isSelected: !column.isSelected };
        return column;
      });
      setDisplayedColumns(updatedDisplayedColumns);
    } else {
      setDisplayedColumns(updatedOriginalColumns);
    }
  };

  const handleSelectAll = () => {
    const allSelected = originalColumns.every((column) => column.isSelected);
    const newSelectionState = !allSelected;

    const updatedOriginalColumns = originalColumns.map((column) => ({
      ...column,
      isSelected: newSelectionState,
    }));
    setOriginalColumns(updatedOriginalColumns);

    if (searchTerm) {
      const updatedDisplayedColumns = displayedColumns.map((column) => ({
        ...column,
        isSelected: newSelectionState,
      }));
      setDisplayedColumns(updatedDisplayedColumns);
    } else {
      setDisplayedColumns(updatedOriginalColumns);
    }
  };

  // Determine select all checkbox state (memoized for performance)
  const { allSelected, someSelected } = useMemo(() => {
    const count = originalColumns.filter((column) => column.isSelected).length;
    return {
      allSelected: count === originalColumns.length,
      someSelected: count > 0 && count < originalColumns.length,
    };
  }, [originalColumns]);

  const handleApply = async () => {
    setApplyLoading(true);
    let selectedColumns = originalColumns
      .filter(({ isSelected }) => isSelected)
      .map(({ key }) => key);

    // Ensure Component column is always included in preferences
    const componentColumn = originalColumns.find(
      (col) => col.name === 'Component' || col.name.includes('Component')
    );
    if (componentColumn && !selectedColumns.includes(componentColumn.key)) {
      selectedColumns = [...selectedColumns, componentColumn.key];
    }

    try {
      await onApply(selectedColumns);
      if (userPreferenceKey && structureId) {
        if (userPreferenceType === 'columns') {
          updatePreferencesMutation({
            variables: {
              input: { structureId, key: userPreferenceKey, columns: selectedColumns },
            },
            refetchQueries: [GetUserPreferencesDocument],
          });
        }

        if (userPreferenceType === 'xlsx') {
          updatePreferencesMutation({
            variables: {
              input: { structureId, key: userPreferenceKey, xlsx: selectedColumns },
            },
            refetchQueries: [GetUserPreferencesDocument],
          });
        }
      }
      if (successMessage) showSnackBar(successMessage, 'success');
    } catch {
      if (errorMessage) showSnackBar(errorMessage, 'error');
    }
    onClose();
    setSearchTerm('');
    setApplyLoading(false);
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { maxWidth: '82rem' } }}>
      <Header>
        <TitleText>{heading}</TitleText>
      </Header>
      <Box sx={{ padding: '0 4.4rem', marginTop: '2rem' }}>
        <TextField
          fullWidth
          placeholder="Search columns..."
          value={searchTerm}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            setSearchTerm(e.target.value.toLowerCase());
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '1.4rem',
            },
            '& .MuiInputBase-input': {
              padding: '1rem',
            },
          }}
        />
      </Box>
      <DialogContent sx={{ paddingLeft: '4.4rem' }}>
        <SubHeading>{subHeading}</SubHeading>
        {/* Select All checkbox on its own line */}
        <Box sx={{ marginBottom: '1.5rem', paddingTop: '1rem' }}>
          <FormControlLabel
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '1.4rem',
                fontWeight: 600,
              },
            }}
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onClick={handleSelectAll}
              />
            }
            label="Select All"
          />
        </Box>

        {/* Individual column checkboxes */}
        <Grid container paddingBottom="4rem" rowSpacing="0.5rem" columnSpacing="1rem">
          {displayedColumns.map(({ name, key, isSelected }) => (
            <Grid item xs={3} key={key}>
              <FormControlLabel
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '1.4rem' } }}
                control={
                  <Checkbox
                    checked={isSelected}
                    onClick={() => handleColumnChange(key)}
                    disabled={name === 'Component'}
                  />
                }
                label={name}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <Footer>
        <DialogActionButton onClick={handleClose}>
          {cancelButtonText ?? 'Cancel'}
        </DialogActionButton>
        <DialogActionButton disabled={applyLoading} onClick={handleApply} contained>
          {applyLoading ? (
            <>
              {applyButtonLoadingText ? (
                <>
                  {applyButtonLoadingText}
                  <CircularProgress size={20} color="inherit" />
                </>
              ) : (
                applyButtonText
              )}
            </>
          ) : (
            applyButtonText
          )}
        </DialogActionButton>
      </Footer>
    </Dialog>
  );
};
